import {HttpClient, HttpParams} from '@angular/common/http';
import {plainToClass} from 'class-transformer';
import {Observable} from 'rxjs';
import {trimEnd, get, defaultsDeep, isObject} from 'lodash';
import {map} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {BaseEntity} from './base.entity';
import {ClassType} from 'class-transformer/ClassTransformer';

interface ApiPagable {
  size: number;
  totalElements: number;
  totalPages: number;
  number: number;
}

interface ApiResponse<T> {
  [key: string]: any;
  page: ApiPagable;
}

interface ApiArrayResponse<T> extends ApiResponse<T> {
  _embeded: {
    [p: string]: T[]
  };
}

export class BaseService<T extends BaseEntity> {

  protected http: HttpClient;

  private readonly apiUrl: string;

  constructor(protected readonly path: string, private readonly type: ClassType<T>) {
    this.apiUrl = `${trimEnd(environment.apiUrl, '/')}/${path}`;
  }

  public get url(): string {
    return this.apiUrl;
  }

  public findById(id: string|number): Observable<T> {
    return this.http.get<ApiResponse<T>>(`${this.apiUrl}/${id}`)
      .pipe(map(data =>  plainToClass(this.type, data)));
  }

  public find(options?: any): Observable<T[]> {
    options = defaultsDeep({
      ...options
    }, {
      params: {
        size: Number.MAX_SAFE_INTEGER.toString()
      }
    });
    return this.http.get<ApiArrayResponse<T>>(`${this.apiUrl}`, options)
      .pipe(map(data =>  plainToClass<T, any>(this.type, get(data, `_embedded.${this.path.split('/').pop()}`))));
  }

  public save(model: T, options?: any): Observable<T> {
    const method = model.id ? 'put' : 'post';
    const url = model.id ? `${this.apiUrl}/${model.id}` : `${this.apiUrl}`;
    options = defaultsDeep({
      ...options,
    }, {
      body: model
    });
    return this.http.request<ApiResponse<T>>(method, url, options)
      .pipe(map(data =>  plainToClass<T, object>(this.type, data)));
  }

  public delete(id: string|number) {
    return this.http.delete<ApiResponse<T>>(`${this.apiUrl}/${this.path}/${id}`);
  }
}
